import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  KeyboardArrowDown,
  CheckCircleOutline,
  PictureAsPdfOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import useVendorAccessGate from "../../../hooks/useVendorAccessGate";
import ConfirmationModal from "../../Shared/confirmationModal";
import { useAuth } from "../../../services/providers/authProvider";
import { httpClient } from "../../../services/httpClient";
import api from "../../../config/gatewayConfig";

const GestioneBudget = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalSuccessOpen, setModalSuccessOpen] = useState(false);
  useVendorAccessGate();
  const navigate = useNavigate();
  const { vendorId } = useAuth() || {};

  useEffect(() => {
    const fetchTransactions = async () => {
      if (!vendorId) return;
      try {
        const response = await httpClient.get(api.getVendorTransactions(vendorId));
        setTransactions(response.data || []);
      } catch (error) {
        console.error("Errore nel recupero delle transazioni:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTransactions();
  }, [vendorId]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("success") === "true") {
      setModalSuccessOpen(true);
      window.history.replaceState({}, document.title, "/gestioneBudget");
    }
  }, []);

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/D";
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString("it-IT", { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatAmount = (amount, currency) => {
    if (amount == null) return "0.00 €";
    return `+${(amount / 100).toFixed(2)} €`;
  };

  const translateStatus = (status) => {
    if (status === "succeeded") return "Completato";
    if (status === "canceled" || status === "failed") return "Annullato";
    return status;
  };

  const getStatusColor = (status) => {
    if (status === "succeeded") return "green";
    if (status === "canceled" || status === "failed") return "gray";
    return "inherit";
  };

  // Apre il PDF della fattura per il download diretto
  const downloadInvoice = (pdfUrl) => {
    if (!pdfUrl) return;
    window.open(pdfUrl, "_blank", "noopener,noreferrer");
  };

  // Intestazioni della tabella movimenti
  const tableHeaders = [
    { id: "description", label: "Descrizione", align: "left" },
    { id: "created", label: "Data e Ora", align: "left" },
    { id: "status", label: "Stato", align: "left" },
    { id: "amount", label: "Importo", align: "right" },
    { id: "invoice", label: "Fattura", align: "center" },
  ];

  // Stile comune delle celle del corpo tabella
  const cellSx = {
    fontFamily: "Rubik, sans-serif",
    color: "#2c3e50",
    borderBottom: "1px solid #eef2f7",
    py: 1.6,
  };

  return (
    <>
      <Box display="flex" flexDirection="column" gap={2} sx={{ width: "100%", fontFamily: "Rubik, sans-serif" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: "bold", color: "#0b3d87", fontFamily: "Rubik, sans-serif" }}>
            Riepilogo Movimenti
          </Typography>
          <Box display="flex" justifyContent="flex-end">
            <Button
              onClick={() => navigate("/ricarica")}
              variant="contained"
              sx={{
                backgroundColor: "#0b3d87",
                color: "white",
                borderRadius: "20px",
                padding: "6px 20px",
                fontWeight: "bold",
                textTransform: "none",
                fontFamily: "Rubik, sans-serif",
                "&:hover": { backgroundColor: "#092b6a" },
              }}
            >
              Effettua una Ricarica
            </Button>
          </Box>
        </Box>

        <Typography variant="subtitle1" sx={{ fontFamily: "Rubik, sans-serif", fontWeight: "bold", color: "#0b3d87", mt: 2 }}>
          FILTRA PER
        </Typography>
        <Box display="flex" gap={2} flexWrap="wrap">
          {["Data inizio intervallo", "Data fine intervallo"].map((filter) => (
            <Button
              key={filter}
              variant="contained"
              endIcon={<KeyboardArrowDown />}
              sx={{
                backgroundColor: "#0b3d87",
                color: "white",
                borderRadius: "7px",
                padding: "6px 15px",
                fontWeight: "bold",
                textTransform: "none",
                fontFamily: "Rubik, sans-serif",
                "&:hover": { backgroundColor: "#092b6a" },
              }}
            >
              {filter}
            </Button>
          ))}
        </Box>

        <Box sx={{ mt: 3 }}>
          {loading ? (
            <Typography sx={{ fontFamily: "Rubik, sans-serif", textAlign: "center", py: 5 }}>
              Caricamento transazioni in corso...
            </Typography>
          ) : transactions.length > 0 ? (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                borderRadius: "12px",
                border: "1px solid #e3e8f0",
                overflow: "hidden",
                boxShadow: "0px 4px 20px rgba(11, 61, 135, 0.05)",
              }}
            >
              <Table sx={{ minWidth: 650 }} aria-label="Tabella movimenti">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#0b3d87" }}>
                    {tableHeaders.map((header) => (
                      <TableCell
                        key={header.id}
                        align={header.align}
                        sx={{
                          color: "white",
                          fontWeight: "bold",
                          fontFamily: "Rubik, sans-serif",
                          fontSize: "0.95rem",
                          borderBottom: "none",
                          py: 1.8,
                        }}
                      >
                        {header.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((item, index) => (
                    <TableRow
                      key={item.id || index}
                      sx={{
                        transition: "background-color 0.2s ease",
                        "&:hover": { backgroundColor: "#f5f8ff" },
                        "&:last-child td": { borderBottom: 0 },
                      }}
                    >
                      <TableCell sx={cellSx}>
                        {item.description || "Ricarica Account"}
                      </TableCell>
                      <TableCell sx={cellSx}>{formatDate(item.created)}</TableCell>
                      <TableCell sx={cellSx}>
                        <Box
                          component="span"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.8,
                            color: getStatusColor(item.status),
                            fontWeight: "bold",
                          }}
                        >
                          ● {translateStatus(item.status)}
                        </Box>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          ...cellSx,
                          fontWeight: "bold",
                          color: getStatusColor(item.status),
                          textDecoration:
                            item.status === "canceled" || item.status === "failed"
                              ? "line-through"
                              : "none",
                        }}
                      >
                        {formatAmount(item.amount, item.currency)}
                      </TableCell>
                      <TableCell align="center" sx={cellSx}>
                        {item.invoicePdfUrl ? (
                          <Tooltip title="Scarica la fattura">
                            <IconButton
                              onClick={() => downloadInvoice(item.invoicePdfUrl)}
                              sx={{ color: "#0b3d87" }}
                              aria-label="Scarica la fattura"
                            >
                              <PictureAsPdfOutlined />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography
                            component="span"
                            sx={{ fontFamily: "Rubik, sans-serif", color: "#aab4c5" }}
                          >
                            —
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography sx={{ fontFamily: "Rubik, sans-serif", textAlign: "center", py: 5 }}>
              Nessun movimento disponibile al momento.
            </Typography>
          )}
        </Box>
      </Box>

        <ConfirmationModal
          open={modalSuccessOpen}
          onClose={() => setModalSuccessOpen(false)}
          title="Pagamento Completato"
          subtitle=""
          message="La tua ricarica è andata a buon fine. Il saldo è stato aggiornato correttamente."
          icon={<CheckCircleOutline sx={{ color: "white", fontSize: 50 }} />}
          hideCancelButton={true}
          confirmText="Chiudi"
          onConfirm={() => setModalSuccessOpen(false)}
        />
    </>
  );
};

export default GestioneBudget;
